<script setup lang="ts">
import { computed } from 'vue'

import ECard from './ECard.vue'
import ESectionHeader from './ESectionHeader.vue'
import ESkeleton from './ESkeleton.vue'
import EEmptyState from './EEmptyState.vue'
import { useUiStrings } from '../lib/strings'

/**
 * A titled card with the three states every data surface actually has: loading,
 * empty and populated. Having them in one place is what stops each module
 * inventing its own spinner-on-white (which §11 forbids) or its own bare
 * "No data" (which teaches nothing).
 */

const props = withDefaults(
  defineProps<{
    title?: string
    description?: string
    loading?: boolean
    /** Show the empty state instead of the default slot. */
    empty?: boolean
    emptyTitle?: string
    emptyDescription?: string
    padding?: 'none' | 'sm' | 'md' | 'lg'
    /** Skeleton rows rendered while loading; match the real content's shape. */
    skeletonRows?: number
  }>(),
  {
    title: '',
    description: '',
    emptyTitle: '',
    emptyDescription: '',
    padding: 'md',
    skeletonRows: 3,
  },
)

const strings = useUiStrings()

const resolvedEmptyTitle = computed(() =>
  props.emptyTitle === '' ? strings.value.panelEmptyTitle : props.emptyTitle,
)
</script>

<template>
  <ECard class="e-panel" padding="none">
    <ESectionHeader
      v-if="title !== '' || $slots.actions"
      class="e-panel__header"
      :title="title"
      :description="description"
      size="sm"
      :level="3"
      divider
    >
      <template v-if="$slots.actions" #actions><slot name="actions" /></template>
    </ESectionHeader>

    <div class="e-panel__body" :class="`e-panel__body--pad-${padding}`">
      <div v-if="loading" class="e-panel__loading" role="status" aria-live="polite">
        <span class="e-visually-hidden">{{ strings.panelLoading }}</span>
        <ESkeleton variant="row" :lines="skeletonRows" />
      </div>

      <EEmptyState
        v-else-if="empty"
        compact
        :title="resolvedEmptyTitle"
        :description="emptyDescription"
      >
        <template v-if="$slots.emptyActions" #actions><slot name="emptyActions" /></template>
      </EEmptyState>

      <slot v-else />
    </div>

    <div v-if="$slots.footer" class="e-panel__footer">
      <slot name="footer" />
    </div>
  </ECard>
</template>

<style>
.e-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.e-panel__header {
  padding: var(--erpx-space-3) var(--erpx-space-4);
}

.e-panel__body {
  flex: 1;
  min-width: 0;
}

.e-panel__body--pad-none { padding: 0; }
.e-panel__body--pad-sm { padding: var(--erpx-space-3); }
.e-panel__body--pad-md { padding: var(--erpx-space-4); }
.e-panel__body--pad-lg { padding: var(--erpx-space-6); }

.e-panel__loading {
  padding: var(--erpx-space-1) 0;
}

.e-panel__footer {
  padding: var(--erpx-space-2) var(--erpx-space-4);
  border-block-start: 1px solid var(--erpx-color-border-subtle);
  background-color: var(--erpx-color-surface-sunken);
  border-end-start-radius: var(--erpx-radius);
  border-end-end-radius: var(--erpx-radius);
}
</style>
